import { enableProdMode } from '@angular/core';
import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';
import * as Sentry from '@sentry/angular';
import { browserSessionIntegration, browserTracingIntegration, replayIntegration } from '@sentry/angular';

import { AppModule } from './app/app.module';
import { environment } from './environments/environment';
import { HttpXhrBackend, HttpClient } from '@angular/common/http';
import { AppConfig, setCsmsAppConfig } from './app/providers/config.service';
import { take } from 'rxjs/operators';

import './ag-license';

/** Helper function to fetch /config.json */
export function getConfigFileAsync(http: HttpClient): Promise<AppConfig> {
    const cacheRemover = Math.floor(new Date().getTime() / (15 * 60)); // allow cache of 15 minutes
    return http.get<AppConfig>(`${window.location.origin}/config.json?${cacheRemover}`).pipe(take(1)).toPromise();
}

/** Bootstrap the application with the given config */
export async function bootstrapApp(config: AppConfig): Promise<void> {
    setCsmsAppConfig(config);

    if (environment.production) {
        enableProdMode();
    }

    try {
        // Initialize Sentry before bootstrapping
        Sentry.init({
            dsn: config.sentryDsn,
            environment: config.envName,
            integrations: [browserTracingIntegration(), browserSessionIntegration(), replayIntegration()],
            // Performance Monitoring
            tracesSampleRate: environment.production ? 0.2 : 1.0, // Capture 20% of transactions in production
            // Session Replay
            replaysSessionSampleRate: 0,
            replaysOnErrorSampleRate: config.sentryReplaySampleRate,
        });

        await platformBrowserDynamic().bootstrapModule(AppModule);
    } catch (err) {
        console.error(err);
        Sentry.captureException(err);
        throw err;
    }
}

// Load up /config.json and bootstrap angular only after that
const http = new HttpClient(new HttpXhrBackend({ build: () => new XMLHttpRequest() }));
getConfigFileAsync(http)
    .then(bootstrapApp)
    .catch((e) => {
        console.error('Could not load /config.json', e);
        Sentry.captureException(e);
    });
